import { userState } from "../../state/UserState";
import { PhonePreview } from "../../components/PhonePreview";
import { useCallback, useEffect, useState } from "react";
import {
  sendSingleUserFlashMessage,
  verifySingleRecipient,
} from "../../api/new-api";
import { debounce } from "lodash";
import { ErrorAlert } from "../../components/alerts";
import { SCHEDULE_NOTIFICATIONS } from "./player-communications-page";
import { useColors } from "../../provider/colors-provider";
import CustomInput from "../../components/CustomInput";
import CustomTextarea from "../../components/CustomTextarea";
import { Alert, Snackbar } from "@mui/material";

type Props = {
  scheduleType: string;
  notificationScheduleTime: Date;
  refetch: () => void;
};

const SingleUserNotification = ({
  scheduleType,
  notificationScheduleTime,
  refetch,
}: Props) => {
  const { primary } = useColors();
  const [notification, setNotification] = useState<any>({
    userId: null,
    title: null,
    message: null,
    badgeNumber: null,
    userFullName: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [badgeVerified, setBadgeVerified] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validate = () => {
    const { title, message, userId } = notification;

    if (!userId) {
      setError("Badge number is required");
      return false;
    }

    if (title === "" || message === "" || !title || !message) {
      setError("Title and message cant be empty");
      return false;
    }
    return true;
  };

  const sendToVenue = async () => {
    setLoading(true);
    try {
      if (scheduleType === SCHEDULE_NOTIFICATIONS.NOW) {
        await sendSingleUserFlashMessage({
          userId: notification.userId,
          title: notification.title,
          message: notification.message,
        });

        const targetText = notification.userFullName
          ? `Success: Notification sent to ${notification.userFullName}`
          : notification.badgeNumber
          ? `Success: Notification sent to member #${notification.badgeNumber}`
          : "Success: Notification sent.";
        setSuccessMessage(targetText);
        setSuccessOpen(true);

        setNotification({
          userId: null,
          title: null,
          message: null,
          badgeNumber: null,
          userFullName: null,
        });
        setBadgeVerified(false);
      }
      // if (scheduleType === SCHEDULE_NOTIFICATIONS.FUTURE) {
      // const { title, message } = notification;
      // await scheduleNotification({
      //   scheduleType: 'OnceInFuture',
      //   notificationType: 'AllVenue',
      //   notificationTitle: title,
      //   notificationMessage: message,
      //   notificationScheduleTime: notificationScheduleTime.getTime(),
      // });
      // }

      await refetch();
    } catch (error) {
      setError("Error sending venue flash message");
      console.error("Error sending venue flash message", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (validate()) {
      sendToVenue();
    }
  };

  const verifyBadgeNumber = async (badgeNumber: string) => {
    try {
      const res = await verifySingleRecipient(badgeNumber);
      if (res.data.verifyStatus === "SUCCESS") {
        setError("");
        setBadgeVerified(true);
        setNotification({
          ...notification,
          userId: res.data.userId,
          userFullName: res.data.userFullName,
          badgeNumber,
        });
        return;
      }

      if (res.data.verifyStatus === "NO_PHONE") {
        setError("This User is NOT signed up to the mobile app");
        setBadgeVerified(false);
        return;
      }

      setError("Badge number not found");
      setBadgeVerified(false);
    } catch (error) {}
  };

  // Create a debounced version of verifyBadgeNumber
  const debouncedVerifyBadgeNumber = useCallback(
    debounce((value) => verifyBadgeNumber(value), 300),
    []
  );

  useEffect(() => {
    setError("");
  }, [notification]);
  console.log(notification);
  if (scheduleType === SCHEDULE_NOTIFICATIONS.FUTURE) {
    return (
      <div className="bg-white flex  items-center justify-center text-center">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-normal leading-6 text-gray-900">
            This feature is not available yet for future notifications. Please
            select "Send Notification Now" to send a notification to a single
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white flex  items-center justify-center text-center pb-14">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          Single User Notification
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>
            Send a notification to a single user. You can search for a user by
            their badge number below.
          </p>
        </div>
        <div>
          <div className="mt-5 sm:flex sm:items-center">
            <div className="w-full sm:max-w-xl">
              <CustomInput
                id="badgeNumber"
                name="badgeNumber"
                type="text"
                placeholder="User Badge Number"
                value={notification.badgeNumber ?? ""}
                onChange={(e: any) => {
                  setBadgeVerified(false);
                  setError("");
                  debouncedVerifyBadgeNumber(e.target.value);
                  setNotification({
                    ...notification,
                    badgeNumber: e.target.value,
                  });
                }}
              />
            </div>
          </div>
        </div>
        <div>
          {badgeVerified && (
            <div>
              <div className="mt-5">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  {`Found Member #${notification.badgeNumber} ${notification.userFullName}`}
                </h3>
              </div>

              <form
                className="mt-5 sm:flex sm:items-center"
                onSubmit={handleSubmit}
              >
                <div className="flex flex-col gap-2 w-full sm:max-w-xl">
                  <CustomInput
                    onChange={(e) =>
                      setNotification({
                        ...notification,
                        title: e.target.value,
                      })
                    }
                    id="title"
                    name="title"
                    type="title"
                    placeholder="Notification Title"
                  />

                  <CustomTextarea
                    onChange={(e: any) =>
                      setNotification({
                        ...notification,
                        message: e.target.value,
                      })
                    }
                    id="message"
                    name="message"
                    placeholder="Notification Message"
                    type="message"
                  />

                  <button
                    type="submit"
                    style={{
                      backgroundColor: primary,
                    }}
                    className="text-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                  >
                    {loading
                      ? "Sending..."
                      : `Send to ${notification.userFullName}`}
                  </button>
                </div>
              </form>
            </div>
          )}
          {error && <ErrorAlert message={error} />}
        </div>
        <div className="mt-5">
          <PhonePreview
            screenType="NOTIFICATION_PREVIEW"
            notificationTitle={notification?.title ?? "Notification Title"}
            notificationMessage={notification?.message ?? "Notification Body"}
            userState={userState}
          />
        </div>
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessOpen(false)}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default SingleUserNotification;
